// When an admin removes a palette colour, designs that used it are drawn (and re-saved) in
// the nearest remaining colour (CIE76 in Lab), mirroring hrth_proto::pixels.
import { INK, type PixelIcon } from './pixels'

const HEX = /^#[0-9a-fA-F]{6}$/

function rgb(hex: string): [number, number, number] | null {
  if (!HEX.test(hex)) return null
  return [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255) as [number, number, number]
}

const linear = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)

/** WCAG relative luminance (0–1), or null for malformed input. */
export function luminance(hex: string): number | null {
  const c = rgb(hex)
  if (!c) return null
  return 0.2126 * linear(c[0]) + 0.7152 * linear(c[1]) + 0.0722 * linear(c[2])
}

/** Lowest luminance with 3:1 contrast (WCAG non-text) against black, and highest against white. */
const MIN_LUMINANCE = 0.1
const MAX_LUMINANCE = 0.3

/** Whether a colour has 3:1 contrast on that theme's background (so is drawn unchanged). */
export function readableOn(hex: string, dark: boolean): boolean {
  const l = luminance(hex)
  return l !== null && (dark ? l >= MIN_LUMINANCE : l <= MAX_LUMINANCE)
}

/**
 * How a palette colour is drawn on a theme, mirroring `hrth_proto::pixels::for_theme`: as it
 * is with 3:1 contrast against the background, else mixed toward white (dark) or black (light)
 * just enough to have it. A neon green stays neon on dark and deepens on light.
 */
export function forTheme(hex: string, dark: boolean): string {
  const c = rgb(hex)
  if (!c || readableOn(hex, dark)) return hex
  const toward = dark ? 1 : 0
  const toHex = (t: number) =>
    '#' + c.map((v) => Math.round((v + (toward - v) * t) * 255).toString(16).padStart(2, '0')).join('')
  // The least mixing that reaches 3:1, checked on whole bytes as drawn.
  let [lo, hi] = [0, 1]
  for (let i = 0; i < 20; i++) {
    const mid = (lo + hi) / 2
    if (readableOn(toHex(mid), dark)) hi = mid
    else lo = mid
  }
  return toHex(hi)
}

function lab(hex: string): [number, number, number] | null {
  const c = rgb(hex)
  if (!c) return null
  const [r, g, b] = c.map(linear) as [number, number, number]
  const x = (0.4124 * r + 0.3576 * g + 0.1805 * b) / 0.95047
  const y = 0.2126 * r + 0.7152 * g + 0.0722 * b
  const z = (0.0193 * r + 0.1192 * g + 0.9505 * b) / 1.08883
  const f = (t: number) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116)
  const [fx, fy, fz] = [f(x), f(y), f(z)]
  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)]
}

/** The palette colour perceptually closest to `hex` (the first on ties). */
export function nearestColor(hex: string, palette: readonly string[]): string | null {
  const a = lab(hex)
  if (!a) return null
  let best: string | null = null
  let bestD = Infinity
  for (const c of palette) {
    const b = lab(c)
    if (!b) continue
    const d = (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2
    if (d < bestD) {
      best = c
      bestD = d
    }
  }
  return best
}

/** The icon with every colour the palette no longer offers replaced by its nearest. */
export function conformIcon(icon: PixelIcon, palette: readonly string[]): PixelIcon {
  if (!palette.length) return icon
  const keep = (c: string) => c === INK || palette.includes(c)
  return { ...icon, colors: icon.colors.map((c) => (keep(c) ? c : (nearestColor(c, palette) ?? c))) }
}
